import { observable, action, computed } from "mobx";
import axios from "axios";
import { makeApi, URLS } from "Utils/apiURL";
import { SERVER_URL } from "Static/constant";
import taskStoreData from "Stores/taskData";
import dialerStoreData from "Stores/DialerData";
import { toasterState } from "Components/common/toaster";
import {
  STATES,
  OUTCOMES,
  DEFAULT_SETTINGS,
  AUTO_LOG_SECONDS,
  resultKind,
  transition,
  nextIndex,
  sessionCounts,
  buildQueue,
  leaveGuard,
  shouldAutoDrop,
  pickPhone,
  validNumbers,
  shouldMarkBad,
  dialSkipReason,
  pendingRetryLog,
  redialItems,
  holdForRedial,
  heldToFlush
} from "Utils/powerDialerMachine";
import { isPowerDialerMock, mockDriver, mockContactOverrides } from "Utils/powerDialerMock";
import { phoneNumberStore } from "Components/ContactCard/phoneNumberStore";
import { cityTimezone } from "Components/ContactCard/LocalTime";
import cityTimezones from "city-timezones";
import { parsePhoneNumber } from "react-phone-number-input";
import { twilioDriver } from "Utils/powerDialerTwilio";
import userDetail from "Stores/userDetail";

const TASK_IDS_KEY = "sgPowerDialerTaskIds";
const POLL_MS = 1000;
const FIND_AHEAD = 2;
const TASK_FETCH_BATCH = 10;
// Outcome labels from the machine -> call disposition internal names (display names are team-editable).
const OUTCOME_KEYS = {
  [OUTCOMES.CONNECTED]: "CONNECTED",
  [OUTCOMES.LEFT_VOICEMAIL]: "LEFT_VOICE_MAIL",
  [OUTCOMES.NO_ANSWER]: "NO_ANSWER",
  [OUTCOMES.BUSY]: "BUSY",
  [OUTCOMES.WRONG_NUMBER]: "WRONG_NUMBER",
  [OUTCOMES.VOICEMAIL_NONE_LEFT]: "VOICEMAIL_NONE_LEFT"
};
const NOT_DIALABLE = ["skipped", "removed", "not-found"];
// Disposition -> the machine's label for the defaults (so counts/chips match), else its display name.
const labelFor = d => Object.keys(OUTCOME_KEYS).find(label => OUTCOME_KEYS[label] === d?.name) || d?.displayName || "";
const isConnectedDisposition = d => d?.name === "CONNECTED" || d?.mapTo === "Answered";

const readTaskIds = () => {
  try {
    return JSON.parse(localStorage.getItem(TASK_IDS_KEY)) || [];
  } catch (e) {
    return [];
  }
};

const data = config => makeApi(config).then(r => r?.data);

// No backend zone: contact city, else the phone's country when that country has a single zone.
const contactTimeZone = (contact = {}) => {
  const byCity = cityTimezone(contact.city, contact.country);
  if (byCity) return byCity;
  try {
    const iso2 = parsePhoneNumber(pickPhone(contact).phone)?.country;
    const zones = new Set(cityTimezones.cityMapping.filter(c => c.iso2 === iso2).map(c => c.timezone));
    return zones.size === 1 ? [...zones][0] : "";
  } catch (e) {
    return "";
  }
};

// Existing APIs, except call-state / voicemail drop / recordings (new dialer-service endpoints).
const api = {
  // /cm/task/filter has no taskIds filter, so rows come from getTask per id.
  taskRows: async (ids = []) => {
    const rows = [];
    for (let i = 0; i < ids.length; i += TASK_FETCH_BATCH) {
      const batch = await Promise.all(
        ids.slice(i, i + TASK_FETCH_BATCH).map(id =>
          data({ url: URLS.getPerformTaskData.replace("<<taskId>>", id) })
            .then(d => d?.tasks?.[0])
            .catch(() => null)
        )
      );
      rows.push(...batch.filter(Boolean));
    }
    return rows;
  },
  dropVoicemail: body => data({ url: `${SERVER_URL}/ds/twilio/voicemail/drop`, method: "POST", data: body }),
  completeTask: body => makeApi({ url: URLS.markAsComplete, method: "POST", data: body }),
  changeOutcome: body => data({ url: URLS.dialerLogCall, method: "POST", data: body }),
  findPhoneNumber: contactId => data({ url: `${URLS.purchasePhoneNumber}/${contactId}` }),
  listRecordings: () => data({ url: `${SERVER_URL}/ds/voicemail/recordings` }),
  savePreferences: preferences => data({ url: URLS.callRecordingSetting, method: "POST", data: { preferences } }),
  uploadRecording: form =>
    axios.post(`${SERVER_URL}/ds/voicemail/recordings`, form, { withCredentials: true }).then(r => r?.data),
  updateRecording: (id, body) => data({ url: `${SERVER_URL}/ds/voicemail/recordings/${id}`, method: "PUT", data: body }),
  deleteRecording: id => data({ url: `${SERVER_URL}/ds/voicemail/recordings/${id}`, method: "DELETE" })
};

class PowerDialerStore {
  // Call task ids the session dials, in due order (set on entry from Tasks).
  @observable taskIds = readTaskIds();
  // Call task ids of the current Tasks view, fetched when the Start menu opens.
  @observable callTaskIds = [];
  @observable callTaskIdsLoading = false;

  @observable sessionId = "";
  @observable startedAt = 0;
  @observable endedAt = 0;
  @observable endReason = "";
  @observable state = null;
  @observable queue = [];
  @observable index = -1;
  @observable callLog = [];
  @observable skipped = [];
  @observable settings = { ...DEFAULT_SETTINGS };
  @observable recordings = [];
  @observable currentCall = null; // {callSid, startedAt, connectedAt, suggestion}
  @observable countdownLeft = 0;
  @observable autoLogLeft = 0;
  @observable loading = false;
  @observable notes = "";
  @observable muted = false;
  @observable wrapupDispositionId = "";
  @observable pauseRequested = false;
  // Queue index the next dial will use, fixed when the countdown starts (-1 = pick at dial time).
  @observable upcomingIndex = -1;
  // Contacts with more than one call task in the selection (each is called once).
  @observable multiTaskContacts = 0;

  // Drivers share one shape: dial(item, ctx) -> {callSid}, hangup, mute, callState(callSid).
  realDriver = twilioDriver;
  settingsLoaded = false;
  timers = {};
  noPhoneMode = "";
  redialed = false;

  get driver() {
    return isPowerDialerMock() ? mockDriver : this.realDriver;
  }

  @computed get current() {
    return this.queue[this.index] || null;
  }

  @computed get counts() {
    return sessionCounts(this.callLog);
  }

  // Everything still ahead of the current call.
  @computed get remainingQueue() {
    return this.queue.slice(this.index + 1).filter(q => ["ready", "finding", "not-found", "no-number"].includes(q.status));
  }

  // Dialable calls only: the badge / progress "X of N".
  // Contacts, not attempts: next-number retries reuse the item and pass-2 redial copies aren't counted.
  @computed get dialableCount() {
    return this.queue.filter(q => !q.pass && !NOT_DIALABLE.includes(q.status)).length;
  }

  @computed get callNumber() {
    const dialed = this.queue.filter(q => !q.pass && q.status === "dialed").length;
    const waiting = this.current && !this.current.pass && this.current.status !== "dialed" ? 1 : 0;
    return Math.min(dialed + waiting, this.dialableCount);
  }

  // The task the Start Task page shows: the current call, or the first dialable one before dialing.
  // A current item not dialed yet (number lookup, or its next number after a no-answer) is dialed again.
  get dialFrom() {
    return this.current && this.current.status !== "dialed" ? this.index - 1 : this.index;
  }

  // Next dialable index; skips contacts that hit a time rule since the queue was built,
  // and re-queues no-answers once when the queue runs out (setting).
  @action
  nextDialable = () => {
    let i = nextIndex(this.queue, this.dialFrom);
    while (i > -1) {
      const reason = dialSkipReason(this.queue[i], this.settings);
      if (!reason) return i;
      this.skipItem(this.queue[i], reason);
      i = nextIndex(this.queue, i);
    }
    if (!this.settings.redialNoAnswersAtEnd || this.redialed) return -1;
    this.redialed = true;
    const extra = redialItems(this.queue, this.callLog);
    if (!extra.length) return -1;
    this.queue.push(...extra);
    return this.nextDialable();
  };

  @computed get focusTaskId() {
    if (this.current) return this.current.taskId;
    const i = nextIndex(this.queue, -1);
    return i > -1 ? this.queue[i].taskId : "";
  }

  @computed get dispositions() {
    return (dialerStoreData?.stageList || []).filter(d => d?.displayEnabled && d?.name !== "NOT_LOGGED");
  }

  @computed get leaveGuard() {
    return leaveGuard(this.state);
  }

  // Done-summary + "View in Calls" window (member + startedAt..endedAt).
  @computed get summary() {
    return {
      startedAt: this.startedAt,
      endedAt: this.endedAt,
      counts: this.counts,
      calls: this.callLog.slice(),
      skipped: this.skipped.slice(),
      notLogged: this.callLog.filter(c => c.notLogged).length
    };
  }

  @computed get active() {
    return !!this.state && this.state !== STATES.DONE;
  }

  @action
  setTaskIds = (ids = []) => {
    this.taskIds = ids;
    try {
      localStorage.setItem(TASK_IDS_KEY, JSON.stringify(ids));
    } catch (e) {}
  };

  @action
  fetchCallTaskIds = async () => {
    const filter = taskStoreData?.filter;
    const viewHasCalls =
      !filter?.length || filter.includes("total") || filter.includes("phone");
    if (!viewHasCalls) {
      this.callTaskIds = [];
      return;
    }
    this.callTaskIdsLoading = true;
    try {
      const response = await makeApi({
        url: URLS.getTaskIds,
        method: "POST",
        data: {
          ...taskStoreData.getTaskFilterPayload(
            "",
            undefined,
            1,
            null,
            null,
            20,
            taskStoreData.dateFilterValue,
            taskStoreData.statusFilterValue,
            "",
            "ids"
          ),
          taskType: ["phone"]
        }
      });
      this.callTaskIds = response?.data?.taskIds || [];
    } catch (e) {
      this.callTaskIds = [];
    }
    this.callTaskIdsLoading = false;
  };

  // The rep's Voicemail / Power Dialer settings (GET /cm/settings/dialerSettings, defaults filled in server-side).
  @action
  loadSettings = async () => {
    const [settings, recordings] = await Promise.all([
      data({ url: URLS.callRecordingSetting }).catch(() => null),
      api.listRecordings().catch(() => [])
    ]);
    this.settings = { ...DEFAULT_SETTINGS, ...(settings?.preferences || {}) };
    this.recordings = Array.isArray(recordings) ? recordings : [];
    this.settingsLoaded = true;
  };

  /**
   * Twilio connect params for voicemail detection / drop + ring time, used by the power
   * driver and click-to-call. The mobile rule for auto drop is applied here, per number.
   */
  voicemailParams = async (phoneType = "") => {
    if (!this.settingsLoaded) await this.loadSettings();
    const s = this.settings;
    const params = { ringSeconds: String(+s.ringSeconds || DEFAULT_SETTINGS.ringSeconds) };
    if (!s.detectVoicemail) return params;
    const recording = this.defaultRecording;
    return {
      ...params,
      detectVoicemail: "true",
      autoDrop: String(!!recording && shouldAutoDrop(s, { phoneType })),
      recordingId: recording?.id || ""
    };
  };

  get defaultRecording() {
    return this.recordings.find(r => r.isDefault) || this.recordings[0] || null;
  }

  // Settings > Dialer > Voicemail / Power Dialer tabs. Only the given fields are saved.
  @action
  saveSettings = async partial => {
    const prev = this.settings;
    this.settings = { ...prev, ...partial };
    const res = await api.savePreferences(partial).catch(() => null);
    if (res?.status !== "success") {
      this.settings = prev;
      toasterState.setToastMsg("Couldn't save the setting. Please try again.", "failure");
    }
    return res?.status === "success";
  };

  @action
  refreshRecordings = async () => {
    const list = await api.listRecordings().catch(() => null);
    if (Array.isArray(list)) this.recordings = list;
  };

  uploadRecording = async (file, name, durationSec) => {
    const form = new FormData();
    form.append("file", file, file.name || `${name}.wav`);
    form.append("name", name);
    if (durationSec) form.append("durationSec", String(durationSec));
    const res = await api.uploadRecording(form).catch(e => e?.response?.data || null);
    if (res?.status === "success") {
      await this.refreshRecordings();
      return true;
    }
    toasterState.setToastMsg(res?.message || "Couldn't save the recording.", "failure");
    return false;
  };

  setDefaultRecording = async id => {
    const r = await api.updateRecording(id, { isDefault: true }).catch(() => null);
    if (r?.id) await this.refreshRecordings();
    else toasterState.setToastMsg("Couldn't set the default recording.", "failure");
  };

  deleteRecording = async id => {
    const r = await api.deleteRecording(id).catch(() => null);
    if (r?.status === "success") await this.refreshRecordings();
    else toasterState.setToastMsg("Couldn't delete the recording.", "failure");
  };

  // Creates the session and queue; the dock then shows the start checks.
  @action
  start = async (taskIds = this.taskIds) => {
    this.reset();
    this.loading = true;
    try {
      const [rows] = await Promise.all([
        api.taskRows(taskIds),
        this.loadSettings(),
        !dialerStoreData?.stageList?.length && dialerStoreData.getCallStageList(false)
      ]);
      mockContactOverrides(rows).forEach(r => {
        if (r?.contact && !r.contact.timeZone) r.contact.timeZone = contactTimeZone(r.contact);
      });
      const queue = buildQueue(rows, this.settings);
      const perContact = {};
      rows
        .filter(r => (r?.task?.touchPoint?.type || r?.task?.taskType) === "phone")
        .forEach(r => {
          const id = r?.contact?.id || r?.task?.contactId;
          perContact[id] = (perContact[id] || 0) + 1;
        });
      this.multiTaskContacts = Object.values(perContact).filter(n => n > 1).length;
      this.queue = queue;
      this.skipped = queue
        .filter(q => q.status === "skipped")
        .map(q => ({ taskId: q.taskId, contactId: q.contactId, contact: q.name, reason: q.reason }));
      // No backend session: the id only tags this run (Calls filter uses member + time window).
      this.sessionId = `pd${Date.now().toString(36)}`;
      this.startedAt = Date.now();
      this.state = STATES.START;
      this.setNoPhoneMode(this.settings.noPhoneNumber);
    } catch (e) {
      toasterState.setToastMsg("Couldn't start the power dialer. Please try again.", "failure");
      this.state = null;
    }
    this.loading = false;
  };

  get noPhoneItems() {
    return this.queue.filter(q => q.status === "no-number");
  }

  // "skip" moves them to skipped now; "find" looks numbers up ahead of the dialer; "ask" waits for the popup.
  @action
  setNoPhoneMode = (mode = "skip") => {
    if (mode === "skip") {
      this.noPhoneItems.forEach(q => this.skipItem(q, "no phone number"));
    } else if (mode === "find") {
      this.noPhoneMode = "find";
      this.findAhead();
    }
  };

  @action
  skipItem = (item, reason = "") => {
    item.status = "skipped";
    item.reason = reason;
    this.skipped.push({ taskId: item.taskId, contactId: item.contactId, contact: item.name, reason });
  };

  @action
  findAhead = () => {
    if (this.noPhoneMode !== "find") return;
    let ahead = 0;
    for (let i = this.index + 1; i < this.queue.length && ahead < FIND_AHEAD; i++) {
      const q = this.queue[i];
      if (!["ready", "finding", "no-number"].includes(q.status)) continue;
      ahead++;
      if (q.status === "no-number") this.findNumber(q);
    }
  };

  @action
  findNumber = async item => {
    item.status = "finding";
    let found = {};
    try {
      found = this.foundPhone(await api.findPhoneNumber(item.contactId), item.invalidPhoneNumbers);
    } catch (e) {}
    this.applyFoundNumber(item, found.phone, found.phoneType);
  };

  foundPhone = (res, invalidPhoneNumbers = []) =>
    (+res?.code === 200 &&
      validNumbers({ ...res.contact, invalidPhoneNumbers: [...(res.contact?.invalidPhoneNumbers || []), ...invalidPhoneNumbers] }, this.settings.numberOrder)[0]) ||
    {};

  @action
  applyFoundNumber = (item, phone = "", phoneType = "phone") => {
    if (phone) {
      Object.assign(item, { phone, phoneType, status: "ready" });
    } else {
      // Stays visible as "No number found · will skip" in Up next; dialing passes over it.
      item.status = "not-found";
      item.reason = "no phone number";
      this.skipped.push({ taskId: item.taskId, contactId: item.contactId, contact: item.name, reason: item.reason });
    }
    this.state === STATES.DIALING && !this.currentCall && this.dial();
  };

  @action
  removeFromQueue = item => {
    if (item && item !== this.current) item.status = "removed";
  };

  @action
  dispatch = (event, ctx = {}) => {
    const item = ctx.item || this.current;
    const { state, log, retry } = transition(this.state, event, {
      settings: this.settings,
      item,
      confidence: this.currentCall?.suggestion?.confidence || 0,
      suggestion: this.currentCall?.suggestion?.summary,
      ...ctx
    });
    this.state = state;
    if ([STATES.WRAPUP, STATES.AUTOLOG].includes(state) && this.currentCall && !this.currentCall.endedAt) {
      this.currentCall.endedAt = Date.now();
    }
    if (state === STATES.WRAPUP) this.preselectOutcome();
    if (retry) {
      Object.assign(item, {
        tried: [...(item.tried || []), item.phone],
        triedCallSid: this.currentCall?.callSid || "",
        ...retry,
        status: "ready"
      });
      this.currentCall = null;
    }
    if (!this.driver?.mock && shouldMarkBad(this.settings, log)) phoneNumberStore.markInvalidPhoneNumber(item.contactId, item.phone);
    if (log) {
      const picked = ctx.dispositionId && this.dispositions.find(d => d.id === ctx.dispositionId);
      this.logCall({
        ...log,
        ...(picked && { connected: isConnectedDisposition(picked) }),
        dispositionId: ctx.dispositionId,
        suggestion: this.currentCall?.suggestion || null
      });
    }
    if (state === STATES.COUNTDOWN) this.startCountdown();
    if (state === STATES.AUTOLOG) this.startAutoLog();
    if (state === STATES.DONE) this.end("completed");
    return state;
  };

  @action
  dial = async () => {
    this.clearTimer("countdown");
    const upcoming = this.queue[this.upcomingIndex];
    const i = upcoming && ["ready", "finding"].includes(upcoming.status) ? this.upcomingIndex : this.nextDialable();
    this.upcomingIndex = -1;
    if (i === -1) return this.end("queue_empty");
    if (!this.hasCredits()) {
      toasterState.setToastMsg("You're out of call credits. Add credits to keep dialing.", "failure");
      this.state = STATES.PAUSED;
      return;
    }
    this.index = i;
    this.notes = "";
    this.muted = false;
    this.findAhead();
    if (this.current.status === "finding") {
      // applyFoundNumber() calls dial() again once the lookup settles.
      this.state = STATES.DIALING;
      this.currentCall = null;
      return;
    }
    if (!this.driver) {
      toasterState.setToastMsg("The dialer isn't ready yet. Please try again.", "failure");
      this.state = STATES.PAUSED;
      return;
    }
    this.dispatch("DIAL");
    const item = this.current;
    item.status = "dialed";
    const dialId = (this.dialSeq = (this.dialSeq || 0) + 1);
    this.currentCall = { callSid: "", startedAt: Date.now(), connectedAt: 0, suggestion: null };
    try {
      const { callSid } = await this.driver.dial(item, {
        dialerSessionId: this.sessionId,
        voicemailParams: await this.voicemailParams(item.phoneType),
        position: this.callLog.length
      });
      // Session ended / reset while the dial was in flight: drop the call.
      if (this.dialSeq !== dialId || !this.currentCall) {
        if (this.driver) this.driver.hangup(callSid);
        return;
      }
      this.currentCall.callSid = callSid;
      this.poll();
    } catch (e) {
      toasterState.setToastMsg("Couldn't place the call. Press Start to try the next one.", "failure");
      this.currentCall = null;
      this.state = STATES.PAUSED;
    }
  };

  poll = () => {
    this.clearTimer("poll");
    this.timers.poll = setInterval(async () => {
      const callSid = this.currentCall?.callSid;
      if (!callSid) return this.clearTimer("poll");
      try {
        this.onCallState(await this.driver.callState(callSid));
      } catch (e) {}
    }, POLL_MS);
  };

  @action
  onCallState = (s = {}) => {
    if (!this.currentCall) return;
    const { callSid } = this.currentCall;
    if (s.suggestion) this.currentCall.suggestion = s.suggestion;
    const kind = resultKind(s);
    const lateVoicemail = this.state === STATES.CONNECTED && kind === "voicemail" && s.status !== "completed";
    if (this.state === STATES.DIALING || lateVoicemail) {
      if (!kind) return;
      if (kind === "connected") this.currentCall.connectedAt = Date.now();
      const state = this.dispatch("RESULT", { kind });
      if (state === STATES.COUNTDOWN) this.endCurrentCall(false);
      // Real calls are auto-dropped by the backend (/amd); only the mock needs telling.
      if (state === STATES.COUNTDOWN && kind === "voicemail" && this.driver?.mock) {
        this.dropVm(callSid, true);
      }
    } else if (this.state === STATES.CONNECTED && s.status === "completed") {
      this.dispatch("HANGUP");
      this.clearTimer("poll");
    }
  };

  endCurrentCall = (hangup = true) => {
    if (hangup && this.currentCall?.callSid && this.driver) this.driver.hangup(this.currentCall.callSid);
    this.clearTimer("poll");
  };

  @action
  hangup = () => {
    this.endCurrentCall();
    this.state === STATES.CONNECTED && this.dispatch("HANGUP");
  };

  @action
  dropVoicemail = () => {
    this.dropVm(this.currentCall?.callSid, false);
    this.clearTimer("poll");
    this.dispatch("DROP");
  };

  dropVm = (callSid, auto) => {
    const body = { callSid, recordingId: this.defaultRecording?.id, auto };
    (this.driver?.mock ? mockDriver.dropVoicemail(body) : api.dropVoicemail(body)).catch(() =>
      toasterState.setToastMsg("Couldn't drop the voicemail.", "failure")
    );
  };

  @action
  skipVoicemail = () => {
    this.endCurrentCall();
    this.dispatch("SKIP");
  };

  hasCredits = () => {
    const f = userDetail?.userFeatureFlag || {};
    const freeMinutes = (+f.usFreeCallingMinutesEligible || 0) - (+f.usFreeCallingMinutesUsed || 0);
    return (+f.dialerCredits || 0) > 0.25 || freeMinutes > 1;
  };

  @action
  toggleMute = () => {
    this.muted = !this.muted;
    if (this.driver?.mute) this.driver.mute(this.currentCall?.callSid, this.muted);
  };

  @action
  setNotes = (value = "") => {
    this.notes = value;
  };

  @action
  setWrapupDisposition = (id = "") => {
    this.wrapupDispositionId = id;
  };

  @action
  requestPause = () => {
    if (this.state === STATES.COUNTDOWN) return this.wait();
    this.pauseRequested = true;
  };

  matchDisposition = (label = "") => {
    const key = OUTCOME_KEYS[label];
    const name = label.toLowerCase();
    const list = dialerStoreData?.stageList || [];
    return (
      (key && list.find(d => d?.name === key)) ||
      list.find(d => (d?.displayName || "").toLowerCase() === name || (d?.name || "").toLowerCase() === name) ||
      null
    );
  };

  @action
  preselectOutcome = () => {
    const d = this.matchDisposition(this.currentCall?.suggestion?.outcome) || this.matchDisposition(OUTCOMES.CONNECTED);
    this.wrapupDispositionId = d?.id || "";
  };

  @action
  logWrapup = () => {
    const d = this.dispositions.find(x => x.id === this.wrapupDispositionId);
    this.dispatch("LOG", {
      outcome: d ? labelFor(d) : OUTCOMES.CONNECTED,
      dispositionId: d?.id,
      notes: this.notes
    });
  };

  @action
  changeAutoLog = () => {
    this.clearTimer("autolog");
    this.dispatch("CHANGE");
  };

  dispositionId = (outcome = "") => this.matchDisposition(outcome)?.id || "";

  // Logging never waits for AI notes; failures are reported but don't block the queue.
  @action
  logCall = async (entry = {}) => {
    const { callSid = "", connectedAt = 0, startedAt = 0, endedAt = 0 } = this.currentCall || {};
    const row = {
      ...entry,
      callSid: entry.callSid || callSid,
      phone: entry.phone || this.current?.phone || "",
      talkSeconds: connectedAt ? Math.round(((endedAt || Date.now()) - connectedAt) / 1000) : 0,
      callSeconds: startedAt ? Math.round(((endedAt || Date.now()) - startedAt) / 1000) : 0,
      at: startedAt || Date.now(),
      held: holdForRedial(this.settings, entry, this.current?.pass)
    };
    this.callLog.push(row);
    this.currentCall = null;
    if (!row.held) await this.complete(row);
  };

  complete = async row => {
    const { callSid = "" } = row;
    if (!dialerStoreData?.stageList?.length) await dialerStoreData.getCallStageList(false);
    try {
      await api.completeTask({
        id: row.taskId,
        type: "phone",
        phoneNumber: row.phone,
        note: row.note,
        callDispositionId: row.dispositionId || this.dispositionId(row.outcome),
        // A mock callSid matches no call log; without one the backend logs the call against the task.
        callSid: callSid.startsWith("CAmock") ? "" : callSid,
        autoLogged: !!row.autoLogged,
        ...(row.confidence != null && { confidence: row.confidence })
      });
    } catch (e) {
      toasterState.setToastMsg(`Couldn't log the call with ${row.name}.`, "failure");
    }
  };

  @action
  changeOutcome = async (row, dispositionId) => {
    const d = this.dispositions.find(x => x.id === dispositionId);
    if (!row || !d) return;
    Object.assign(row, {
      outcome: labelFor(d),
      dispositionId: d.id,
      connected: isConnectedDisposition(d),
      voicemailDropped: d.name === "LEFT_VOICE_MAIL" && !!row.voicemailDropped
    });
    if (row.held && row.outcome !== OUTCOMES.NO_ANSWER) {
      const copies = this.queue.filter(q => q.pass && q.taskId === row.taskId);
      copies.forEach(q => q.status === "ready" && (q.status = "removed"));
      if (!copies.some(q => q.status === "dialed")) {
        row.held = false;
        return this.complete(row);
      }
    }
    // ponytail: mock calls have no call log to update, so the change stays local; real calls use /cm/phone/outcome.
    if (!row.callSid || row.callSid.startsWith("CAmock")) return;
    try {
      await api.changeOutcome({ callSid: row.callSid, callDispositionId: d.id, notes: row.note });
    } catch (e) {
      toasterState.setToastMsg("Couldn't change the outcome.", "failure");
    }
  };

  // Done summary: look up a skipped contact's number (existing enrichment, uses credits).
  @action
  findSkippedNumber = async skippedRow => {
    skippedRow.finding = true;
    try {
      const { phone } = this.foundPhone(
        await api.findPhoneNumber(skippedRow.contactId),
        this.queue.find(q => q.contactId === skippedRow.contactId)?.invalidPhoneNumbers
      );
      toasterState.setToastMsg(phone ? `Found a number for ${skippedRow.contact}.` : `No number found for ${skippedRow.contact}.`, phone ? "success" : "info");
      skippedRow.found = !!phone;
    } catch (e) {
      toasterState.setToastMsg("Couldn't look up the number.", "failure");
    }
    skippedRow.finding = false;
  };

  @action
  startCountdown = () => {
    this.clearTimer("countdown");
    // What the countdown shows as "Calling next" is exactly what dial() will call.
    this.upcomingIndex = this.nextDialable();
    if (this.upcomingIndex === -1) return this.dispatch("EMPTY");
    if (this.pauseRequested) {
      this.pauseRequested = false;
      return this.dispatch("WAIT");
    }
    const seconds = +this.settings.countdownSeconds || 0;
    if (!seconds) return this.dial();
    this.countdownLeft = seconds;
    this.timers.countdown = setInterval(
      action(() => {
        this.countdownLeft -= 1;
        this.countdownLeft <= 0 && this.dial();
      }),
      1000
    );
  };

  @action
  wait = () => {
    this.clearTimer("countdown");
    this.dispatch("WAIT");
  };

  @action
  startAutoLog = () => {
    this.clearTimer("autolog");
    this.autoLogLeft = AUTO_LOG_SECONDS;
    this.timers.autolog = setInterval(
      action(() => {
        this.autoLogLeft -= 1;
        if (this.autoLogLeft <= 0) {
          this.clearTimer("autolog");
          this.dispatch("TIMEOUT", { outcome: this.currentCall?.suggestion?.outcome, notes: this.notes });
        }
      }),
      1000
    );
  };

  @action
  logAutoNow = () => {
    this.clearTimer("autolog");
    this.dispatch("LOG", { outcome: this.currentCall?.suggestion?.outcome, notes: this.notes });
  };

  @action
  end = (endReason = "ended") => {
    if (!this.sessionId || this.endedAt) return;
    if (this.currentCall && this.current) {
      // Ended mid-call / before logging: nothing is logged, the task stays due.
      this.callLog.push({
        taskId: this.current.taskId,
        contactId: this.current.contactId,
        name: this.current.name,
        phone: this.current.phone,
        outcome: "Not logged",
        notLogged: true,
        callSid: this.currentCall.callSid,
        talkSeconds: this.currentCall.connectedAt ? Math.round((Date.now() - this.currentCall.connectedAt) / 1000) : 0,
        at: this.currentCall.startedAt
      });
    }
    const pending = !this.currentCall && pendingRetryLog(this.current);
    if (pending) this.logCall(pending);
    this.endCurrentCall();
    heldToFlush(this.callLog).forEach(row => {
      row.held = false;
      this.complete(row);
    });
    this.dialSeq = (this.dialSeq || 0) + 1;
    Object.keys(this.timers).forEach(this.clearTimer);
    twilioDriver.destroy();
    this.state = STATES.DONE;
    this.endedAt = Date.now();
    this.endReason = endReason;
  };

  // Leaving mid-call: a connected call is logged as Connected (spec B5b); anything else stays "Not logged".
  @action
  leave = () => {
    if (this.state === STATES.CONNECTED) {
      this.pauseRequested = true;
      this.hangup();
      this.clearTimer("autolog");
      this.dispatch("LOG", { outcome: OUTCOMES.CONNECTED, notes: this.notes });
    }
    this.end("left");
  };

  clearTimer = name => {
    clearInterval(this.timers[name]);
    delete this.timers[name];
  };

  // Every session starts clean (repeat invocations must not inherit flags).
  @action
  reset = () => {
    Object.keys(this.timers).forEach(this.clearTimer);
    twilioDriver.destroy();
    this.dialSeq = (this.dialSeq || 0) + 1;
    Object.assign(this, {
      sessionId: "",
      startedAt: 0,
      endedAt: 0,
      endReason: "",
      state: null,
      queue: [],
      index: -1,
      callLog: [],
      skipped: [],
      currentCall: null,
      countdownLeft: 0,
      autoLogLeft: 0,
      noPhoneMode: "",
      redialed: false,
      loading: false,
      notes: "",
      muted: false,
      wrapupDispositionId: "",
      pauseRequested: false,
      upcomingIndex: -1,
      multiTaskContacts: 0
    });
  };
}

const powerDialerStore = new PowerDialerStore();
export default powerDialerStore;
export { powerDialerStore };
